<template>
  <section class="mt-4 rounded-token-lg border border-gray-stack-3 dark:border-white/10 p-4 space-y-4">
    <div class="flex items-start justify-between gap-4">
      <div>
        <h4 class="text-sm font-medium text-gray-900 dark:text-gray-50">Unpaid deposit automation</h4>
        <p class="text-sm text-gray-600 dark:text-gray-300">Remind customers, release unpaid time slots, then cancel if the deposit is still unpaid.</p>
      </div>
      <Switch :model-value="value.enabled === true" @update:model-value="update('enabled', $event)"
        aria-label="Unpaid deposit automation"
        :class="[value.enabled ? 'bg-theme-primary-500' : 'bg-gray-200 dark:bg-gray-600', 'relative inline-flex h-6 w-11 shrink-0 rounded-full transition-colors']">
        <span :class="[value.enabled ? 'translate-x-5' : 'translate-x-0', 'inline-block h-5 w-5 transform rounded-full bg-white shadow mt-0.5 ml-0.5 transition']" />
      </Switch>
    </div>
    <div v-if="value.enabled" class="space-y-4">
      <label v-for="field in fields" :key="field.key" class="block text-sm text-gray-900 dark:text-gray-100">
        <span class="block font-medium mb-1">{{ field.label }}</span>
        <div class="flex items-center gap-2">
          <input :aria-label="field.label" type="number" min="1" :max="units[field.key] === 'days' ? 30 : 720" step="1"
            :value="displayValue(field.key)" @change="setDelay(field.key, $event.target.value)"
            class="w-24 rounded-md border-gray-300 dark:bg-gray-800 dark:border-gray-600" />
          <select :value="units[field.key]" @change="setUnit(field.key, $event.target.value)" :aria-label="`${field.label} unit`" class="rounded-md border-gray-300 dark:bg-gray-800 dark:border-gray-600">
            <option value="hours">Hours</option><option value="days">Days</option>
          </select>
        </div>
        <span class="block text-xs text-gray-500 mt-1">{{ field.description }}</span>
      </label>
      <p v-if="error" role="alert" class="text-sm text-red-600">{{ error }}</p>
      <p class="text-xs text-gray-500 dark:text-gray-400">Applies to newly sent deposit texts. The second delay starts after the reminder is sent. Checks run about every five minutes. Texts can be sent at any hour. Turning this off stops pending automation. A paid deposit stops cancellation; a released slot can only be secured again if it is still available.</p>
      <details class="text-xs text-gray-600 dark:text-gray-300">
        <summary class="cursor-pointer font-medium">Customer messages</summary>
        <p class="mt-2">Reminder: Your booking is not yet secured because the deposit has not been paid. Your time slot is now available for others to book. Please pay using your deposit link before the cancellation deadline.</p>
        <p class="mt-2">Cancellation: Your appointment has been cancelled because the deposit was not received in time. Please contact us if you would like to book another time.</p>
      </details>
    </div>
  </section>
</template>

<script setup>
import { computed, reactive, ref } from 'vue';
import { Switch } from '@headlessui/vue';
const props = defineProps({ modelValue: { type: Object, default: () => ({}) } });
const emit = defineEmits(['update:modelValue']);
const value = computed(() => ({ enabled: false, reminderHours: 2, cancellationHours: 2, ...props.modelValue }));
const units = reactive({ reminderHours: 'hours', cancellationHours: 'hours' });
const error = ref('');
const fields = [
  { key: 'reminderHours', label: 'Remind and unlock after', description: 'Time from the successful deposit text to the reminder and slot release.' },
  { key: 'cancellationHours', label: 'Cancel after another', description: 'Additional time after the reminder, if the deposit remains unpaid. A cancellation text is sent automatically.' },
];
const update = (key, next) => emit('update:modelValue', { ...value.value, [key]: next });
const displayValue = key => value.value[key] / (units[key] === 'days' ? 24 : 1);
const setUnit = (key, unit) => {
  const amount = displayValue(key);
  units[key] = unit;
  setDelay(key, Math.min(unit === 'days' ? 30 : 720, Math.max(1, amount)));
};
const setDelay = (key, raw) => {
  const hours = Number(raw) * (units[key] === 'days' ? 24 : 1);
  if (!Number.isFinite(hours) || hours < 1 || hours > 720) {
    error.value = 'Choose a delay from 1 hour to 30 days.';
    return;
  }
  error.value = '';
  update(key, hours);
};
</script>
